/**
 * Keep JSON-LD shape aligned with scripts/ld-graph.mjs (used at prerender time).
 */
import type { BlogPost } from './blog-types';
import type { ServiceSeoPage } from './service-seo-types';
import { brandLogomarkAbsoluteUrl } from './brand-assets';
import { canonicalUrl, getSiteUrl } from './site-url';

export function buildBlogPostLdGraph(post: BlogPost) {
  const siteUrl = getSiteUrl();
  const canonical = canonicalUrl(`/blog/${post.slug}`);
  const orgId = `${siteUrl}/#organization`;
  const logoMark = brandLogomarkAbsoluteUrl();

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
        logo: { '@type': 'ImageObject', url: logoMark },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: post.title,
        isPartOf: { '@id': orgId },
        description: post.metaDescription,
      },
      {
        '@type': 'BlogPosting',
        '@id': `${canonical}#article`,
        headline: post.title,
        description: post.metaDescription,
        datePublished: post.datePublished,
        dateModified: post.dateModified,
        author: { '@type': 'Organization', name: 'Yes We Can LLC', url: siteUrl },
        publisher: {
          '@type': 'Organization',
          name: 'Yes We Can LLC',
          logo: { '@type': 'ImageObject', url: logoMark },
        },
        mainEntityOfPage: { '@id': `${canonical}#webpage` },
        url: canonical,
        image: [logoMark],
        articleSection: post.category,
        keywords: post.keywords?.join(', '),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: canonical },
        ],
      },
    ],
  };
}

export function buildBlogIndexLdGraph(page: number) {
  const siteUrl = getSiteUrl();
  const path = page <= 1 ? '/blog' : `/blog?page=${page}`;
  const canonical = canonicalUrl(path);
  const orgId = `${siteUrl}/#organization`;
  const name = page <= 1 ? 'Blog — Yes We Can LLC' : `Blog (page ${page}) — Yes We Can LLC`;
  const desc =
    page <= 1
      ? 'Guides and overviews on visas, cross-border taxes, business setup, and relocation—informational only, not legal advice.'
      : `More blog articles (page ${page})—visas, taxes, business, and relocation topics. Informational only.`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name,
        isPartOf: { '@id': orgId },
        description: desc,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
        ],
      },
    ],
  };
}

/** Programmatic /services/:slug landing (keep in sync with scripts/ld-graph.mjs). */
export function buildServiceSeoPageLdGraph(page: ServiceSeoPage) {
  const siteUrl = getSiteUrl();
  const canonical = canonicalUrl(`/services/${page.slug}`);
  const orgId = `${siteUrl}/#organization`;
  const logoMark = brandLogomarkAbsoluteUrl();
  const serviceId = `${canonical}#service`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
        logo: { '@type': 'ImageObject', url: logoMark },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: page.title,
        isPartOf: { '@id': orgId },
        description: page.metaDescription,
        mainEntity: { '@id': serviceId },
      },
      {
        '@type': 'Service',
        '@id': serviceId,
        name: page.h1,
        description: page.metaDescription,
        provider: { '@id': orgId },
        serviceType: page.category,
        url: canonical,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
          { '@type': 'ListItem', position: 3, name: page.title, item: canonical },
        ],
      },
    ],
  };
}

export function buildServicesHubLdGraph(pageNum: number) {
  const siteUrl = getSiteUrl();
  const path = pageNum <= 1 ? '/services' : `/services?page=${pageNum}`;
  const canonical = canonicalUrl(path);
  const orgId = `${siteUrl}/#organization`;
  const name =
    pageNum <= 1 ? 'Services — Yes We Can LLC' : `Services (page ${pageNum}) — Yes We Can LLC`;
  const desc =
    pageNum <= 1
      ? 'What Yes We Can LLC can help with: software, web and mobile apps, immigration-related coordination, taxes, design, imports, and more—or introductions to partners when you need them.'
      : `More service pages (page ${pageNum})—software, immigration-related coordination, taxes, design, and more.`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name,
        isPartOf: { '@id': orgId },
        description: desc,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        ],
      },
    ],
  };
}

export function buildTermsLdGraph() {
  const siteUrl = getSiteUrl();
  const canonical = canonicalUrl('/terms');
  const orgId = `${siteUrl}/#organization`;
  const logoMark = brandLogomarkAbsoluteUrl();
  const name = 'Terms of Use — Yes We Can LLC';
  const description =
    'Terms of use for Yes We Can LLC: services, fees, refunds, limitations, and how site content relates to professional advice.';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
        logo: { '@type': 'ImageObject', url: logoMark },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name,
        isPartOf: { '@id': orgId },
        description,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Terms of Use', item: canonical },
        ],
      },
    ],
  };
}

export function buildPrivacyLdGraph() {
  const siteUrl = getSiteUrl();
  const canonical = canonicalUrl('/privacy');
  const orgId = `${siteUrl}/#organization`;
  const logoMark = brandLogomarkAbsoluteUrl();
  const name = 'Privacy Policy — Yes We Can LLC';
  const description =
    'Privacy policy for Yes We Can LLC: what we collect, how we use it, retention, your choices, and third-party tools such as scheduling.';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
        logo: { '@type': 'ImageObject', url: logoMark },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name,
        isPartOf: { '@id': orgId },
        description,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: canonical },
        ],
      },
    ],
  };
}

export function buildContactLdGraph() {
  const siteUrl = getSiteUrl();
  const canonical = canonicalUrl('/contact');
  const orgId = `${siteUrl}/#organization`;
  const logoMark = brandLogomarkAbsoluteUrl();
  const name = 'Contact — Yes We Can LLC';
  const description =
    'Contact Yes We Can LLC: book a consult, email support@yeswecanhq.com, WhatsApp or call +1 (319) 296-5799, Facebook, and about the team. Based in Iowa, United States.';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Yes We Can LLC',
        url: siteUrl,
        logo: { '@type': 'ImageObject', url: logoMark },
      },
      {
        '@type': 'ContactPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name,
        isPartOf: { '@id': orgId },
        description,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: canonical },
        ],
      },
    ],
  };
}

